import * as React from 'react';
import { styled, type StyledComponentWithRef, type StyledComponent } from '@glitz/react';
import { createMinLargeViewportDecorator, createMinMediumViewportDecorator } from 'webapp/viewport';

export const ContentWidth = {
  Huge: 3440,
  VeryWide: 1920,
  Wide: 1600,
  Normal: 1440,
  NarrowNormal: 1024,
  Narrow: 840,
  ExtraNarrow: 620,
} as const;

export function mapContentWidth(contentWidth: Excite.ContentApi.ContentWidth) {
  switch (contentWidth) {
    case 'full':
      return undefined;
    case 'wide':
      return ContentWidth.Wide;
    case 'normal':
      return ContentWidth.Normal;
    case 'narrow':
      return ContentWidth.Narrow;
    case 'extraNarrow':
      return ContentWidth.ExtraNarrow;
    default:
      return ContentWidth.Normal;
  }
}

export const contentWidthDecorator = (contentWidth: Excite.ContentApi.ContentWidth) => {
  switch (contentWidth) {
    case 'full':
      return undefined;
    case 'wide':
      return maxContentWideWidthDecorator;
    case 'normal':
      return maxContentNormalWidthDecorator;
    case 'narrow':
      return maxContentNarrowWidthDecorator;
    case 'extraNarrow':
      return maxContentExtraNarrowWidthDecorator;
    default:
      return maxContentNormalWidthDecorator;
  }
};

export function createLayout<TProps, TInstance>(
  Component: StyledComponentWithRef<TProps, TInstance>,
): StyledComponentWithRef<TProps, TInstance>;

export function createLayout<TProps>(Component: StyledComponent<TProps>): StyledComponent<TProps>;

export function createLayout<TProps, TInstance>(Component: StyledComponentWithRef<TProps, TInstance>) {
  return styled(
    React.forwardRef<TInstance, TInstance>(function Layout({ ...restProps }, ref) {
      const dynamic = React.useMemo(() => styled({ width: '100%' }), []);

      return <Component {...(restProps as any)} css={dynamic} ref={ref} />;
    }),
  );
}

export const Main = createLayout(styled.Main);
export const Block = createLayout(styled.Div);
export const List = createLayout(styled.Ul);

export const edgeMarginsDecorator = styled(
  createMinLargeViewportDecorator({
    padding: { x: t => t.sharedSpacing.xxl },
  }),
  {
    padding: { x: t => t.sharedSpacing.m },
  },
);

export const desktopEdgeMarginDecorator = styled(
  createMinLargeViewportDecorator({
    padding: { x: t => t.sharedSpacing.xxl },
  }),
);

export const maxContentNormalWidthDecorator = styled(
  createMinLargeViewportDecorator({
    margin: { x: 'auto' },
    maxWidth: ContentWidth.Normal + 'px',
  }),
  { width: '100%' },
);

export const maxContentNarrowNormalWidthDecorator = styled(
  createMinLargeViewportDecorator({
    margin: { x: 'auto' },
    maxWidth: ContentWidth.NarrowNormal + 'px',
  }),
  { width: '100%' },
);

export const maxContentNarrowWidthDecorator = styled(
  createMinMediumViewportDecorator({
    margin: { x: 'auto' },
    maxWidth: ContentWidth.Narrow + 'px',
  }),
  { width: '100%' },
);

export const maxContentExtraNarrowWidthDecorator = styled(
  createMinMediumViewportDecorator({
    margin: { x: 'auto' },
    maxWidth: ContentWidth.ExtraNarrow + 'px',
  }),
  { width: '100%' },
);

export const maxContentWideWidthDecorator = styled(
  createMinMediumViewportDecorator({
    margin: { x: 'auto' },
    maxWidth: ContentWidth.Wide + 'px',
  }),
  { width: '100%' },
);

export const maxContentVeryWideWidthDecorator = styled(
  createMinMediumViewportDecorator({
    margin: { x: 'auto' },
    maxWidth: ContentWidth.VeryWide + 'px',
  }),
  { width: '100%' },
);

export const maxContentHugeWidthDecorator = styled(
  createMinMediumViewportDecorator({
    margin: { x: 'auto' },
    maxWidth: ContentWidth.Huge + 'px',
  }),
  { width: '100%' },
);
